import React, { useMemo } from 'react';
import { toServiceDate, fmtRangeShort } from './schedulingUtils';

// Enabled = claimed by this driver (any of completed/current/upcoming).
// Disabled = still open for any driver to claim.
const CHIP = {
  enabled:  { bg: '#DCFCE7', text: '#15803D', border: '1px solid #86EFAC' },
  disabled: { bg: '#FEF9C3', text: '#854D0E', border: '1px dashed #FDE047' },
};

function RouteChip({ route, light, onClick }) {
  const status = route.claimed ? 'enabled' : 'disabled';
  const c = CHIP[status];
  const closed = route.IsInactive === true || route.IsInactive === 1;
  const label = fmtRangeShort(route.StartDateTime, route.EndDateTime) || '—';
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
      style={{
        padding: '3px 5px',
        borderRadius: 4,
        marginTop: 2,
        background: light ? 'rgba(255,255,255,0.22)' : c.bg,
        border: light ? '1px solid rgba(255,255,255,0.28)' : c.border,
        opacity: closed ? 0.5 : 1,
        overflow: 'hidden',
        cursor: 'pointer',
      }}
    >
      <span style={{
        display: 'block',
        fontSize: 10,
        fontWeight: 600,
        color: light ? 'rgba(255,255,255,0.92)' : c.text,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        lineHeight: 1.4,
      }}>
        {label}
      </span>
    </div>
  );
}

// 7-column grid, 2 rows, no day-of-week header.
// Row 1: today−6 … today (today is the last cell of row 1).
// Row 2: today+1 … today+7.
// Clicking a day cell OR any route chip fires onSelect → opens day detail sheet.
function MonthCalendar({ routesByDay, selectedKey, onDaySelect, onRouteSelect }) {
  const now = new Date();
  const todayKey = toServiceDate(now);

  const { cells, rangeLabel } = useMemo(() => {
    const y = now.getFullYear();
    const m = now.getMonth();
    const d = now.getDate();

    // Row 1: today-6 … today  (7 cells, today is last)
    // Row 2: today+1 … today+7 (7 cells)
    const startDate = new Date(y, m, d - 6);
    const endDate   = new Date(y, m, d + 7);

    const sM = startDate.toLocaleDateString([], { month: 'short' });
    const eM = endDate.toLocaleDateString([], { month: 'short' });
    const eY = endDate.getFullYear();
    const rangeLabel = sM === eM
      ? `${sM} ${startDate.getDate()} – ${endDate.getDate()}, ${eY}`
      : `${sM} ${startDate.getDate()} – ${eM} ${endDate.getDate()}, ${eY}`;

    const cells = [];
    for (let i = -6; i <= 7; i++) cells.push(new Date(y, m, d + i));

    return { cells, rangeLabel };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      background: '#fff',
      borderRadius: 20,
      boxShadow: '0 1px 12px rgba(15,23,42,0.08)',
      overflow: 'hidden',
    }}>
      {/* Date-range header */}
      <div style={{ padding: '14px 16px 10px', textAlign: 'center' }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: '#0F172A', letterSpacing: -0.2 }}>
          {rangeLabel}
        </span>
      </div>

      {/* 7-column day grid — 14 cells = exactly 2 rows */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gridAutoRows: '90px',
        gap: 4,
        padding: '0 10px 14px',
      }}>
        {cells.map((date) => {
          const key = toServiceDate(date);
          const dayRoutes = routesByDay[key] || [];
          const isToday    = key === todayKey;
          const isSelected = key === selectedKey;
          const isPast     = key < todayKey;

          return (
            <button
              key={key}
              type="button"
              onClick={() => onDaySelect(key)}
              style={{
                height: '100%',
                padding: '4px 3px',
                borderRadius: 10,
                border: isSelected && !isToday
                  ? '2px solid #3B5BDB'
                  : '2px solid transparent',
                background: isToday
                  ? '#3B5BDB'
                  : isSelected
                  ? '#EFF6FF'
                  : 'transparent',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                overflow: 'hidden',
                opacity: isPast ? 0.55 : 1,
                boxSizing: 'border-box',
              }}
            >
              {/* Day number + short weekday */}
              <div style={{ textAlign: 'center' }}>
                <span style={{
                  fontSize: 12,
                  fontWeight: isToday || isSelected ? 700 : 500,
                  color: isToday ? '#fff' : isSelected ? '#3B5BDB' : isPast ? '#94A3B8' : '#334155',
                  lineHeight: 1.6,
                }}>
                  {date.getDate()}
                </span>
              </div>

              {/* Route chips — clicking a chip opens the detail dialog */}
              {dayRoutes.slice(0, 3).map((r) => (
                <RouteChip
                  key={r.RouteId || r.routeId || r.RouteName}
                  route={r}
                  light={isToday}
                  onClick={() => onRouteSelect(key)}
                />
              ))}

              {/* Overflow count */}
              {dayRoutes.length > 3 && (
                <div style={{
                  fontSize: 9, fontWeight: 600, textAlign: 'center', marginTop: 2,
                  color: isToday ? 'rgba(255,255,255,0.65)' : '#CBD5E1',
                }}>
                  +{dayRoutes.length - 3}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default MonthCalendar;
